import React, { useMemo, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Search, Star, AlertCircle, PhoneOff, X } from 'lucide-react';
import { ProcessedOfficer, TIER_LABELS, TierKey, formatHm, getInitials } from '../lib/officerUtils';
export type RosterSort = 'production' | 'submissions' | 'leads' | 'login' | 'name';
interface OfficerRosterProps {
  officers: ProcessedOfficer[];
  onSelect: (name: string) => void;
}
const TIER_ORDER: TierKey[] = ['pacesetter', 'climbing', 'steady', 'coaching'];
const TIER_DOT: Record<TierKey, string> = {
  pacesetter: '#28A745',
  climbing: '#003B70',
  steady: '#E9ECEF',
  coaching: '#DC3545'
};
const SEGMENTS: Array<{
  key: keyof Pick<ProcessedOfficer, 'borrowerMin' | 'availableMin' | 'wrapUpMin' | 'offQueueMin' | 'ringingMin'>;
  color: string;
}> = [{
  key: 'borrowerMin',
  color: '#28A745'
}, {
  key: 'availableMin',
  color: '#17A2B8'
}, {
  key: 'wrapUpMin',
  color: '#FFC107'
}, {
  key: 'offQueueMin',
  color: '#DC3545'
}, {
  key: 'ringingMin',
  color: 'rgba(51,51,51,0.22)'
}];
const DEFAULT_COLLAPSED: Record<TierKey, boolean> = {
  pacesetter: false,
  climbing: false,
  steady: true,
  coaching: false
};
export function OfficerRoster({
  officers,
  onSelect
}: OfficerRosterProps) {
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<RosterSort>('production');
  const [collapsed, setCollapsed] = useState<Record<TierKey, boolean>>(DEFAULT_COLLAPSED);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return officers;
    return officers.filter(o => o.name.toLowerCase().includes(q));
  }, [officers, search]);
  const sortFn = useMemo(() => {
    switch (sortBy) {
      case 'production':
        return (a: ProcessedOfficer, b: ProcessedOfficer) => b.productionScore - a.productionScore;
      case 'submissions':
        return (a: ProcessedOfficer, b: ProcessedOfficer) => b.submissions - a.submissions;
      case 'leads':
        return (a: ProcessedOfficer, b: ProcessedOfficer) => b.leadsConverted - a.leadsConverted;
      case 'login':
        return (a: ProcessedOfficer, b: ProcessedOfficer) => b.loginMin - a.loginMin;
      case 'name':
        return (a: ProcessedOfficer, b: ProcessedOfficer) => a.name.localeCompare(b.name);
    }
  }, [sortBy]);
  const grouped = useMemo(() => {
    const map: Record<TierKey, ProcessedOfficer[]> = {
      pacesetter: [],
      climbing: [],
      steady: [],
      coaching: []
    };
    filtered.forEach(o => map[o.tier].push(o));
    TIER_ORDER.forEach(t => map[t].sort(sortFn));
    return map;
  }, [filtered, sortFn]);
  const toggleTier = (t: TierKey) => {
    setCollapsed(prev => ({
      ...prev,
      [t]: !prev[t]
    }));
  };
  return <section className="w-full px-8 lg:px-12 xl:px-16 pb-16" data-id="element-173">
      {/* Header */}
      <div className="flex items-baseline justify-between border-b border-ink-10 pb-6 mb-6" data-id="element-174">
        <h2 className="font-serif font-bold text-3xl text-primary" data-id="element-175">
          The Roster
        </h2>
        <SortControl sortBy={sortBy} onChange={setSortBy} data-id="element-176" />
      </div>

      {/* Search + Legend */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8" data-id="element-177">
        <div className="relative w-full md:w-80" data-id="element-178">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-30" strokeWidth={1.5} data-id="element-179" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search loan officers" className="w-full pl-10 pr-9 py-2.5 bg-bone border border-ink-10 hover:border-ink-30 focus:border-ink focus:outline-none transition-colors text-sm text-ink placeholder:text-ink-30 rounded-sm font-sans" data-id="element-180" />
          {search && <button type="button" onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full flex items-center justify-center text-ink-30 hover:text-ink hover:bg-ink-5 transition-colors" aria-label="Clear search" data-id="element-181">
              <X className="w-3 h-3" strokeWidth={2} data-id="element-182" />
            </button>}
        </div>

        <div className="flex items-center gap-4 text-[10px] uppercase tracking-[0.22em] text-ink-50" data-id="element-183">
          <LegendSwatch color="#28A745" label="On Call" data-id="element-184" />
          <LegendSwatch color="#17A2B8" label="Ready" data-id="element-185" />
          <LegendSwatch color="#FFC107" label="Wrap-up" data-id="element-186" />
          <LegendSwatch color="#DC3545" label="Not Ready" data-id="element-187" />
        </div>
      </div>

      {/* Tier Sections */}
      <div className="space-y-3" data-id="element-188">
        {TIER_ORDER.map(tier => {
        const tierOfficers = grouped[tier];
        if (tierOfficers.length === 0) return null;
        return <TierSection key={tier} tier={tier} officers={tierOfficers} collapsed={collapsed[tier]} onToggle={() => toggleTier(tier)} onSelect={onSelect} data-id="element-189" />;
      })}

        {filtered.length === 0 && <div className="text-center py-16 text-ink-50" data-id="element-190">
            <div className="font-serif text-lg mb-2" data-id="element-191">No officers found</div>
            <div className="text-sm" data-id="element-192">Try a different search term.</div>
          </div>}
      </div>
    </section>;
}
function LegendSwatch({
  color,
  label
}: {
  color: string;
  label: string;
}) {
  return <span className="inline-flex items-center gap-1.5" data-id="element-193">
      <span className="w-2 h-2 rounded-full" style={{
      backgroundColor: color
    }} data-id="element-194" />
      <span className="text-ink-70 normal-case tracking-normal text-xs" data-id="element-195">
        {label}
      </span>
    </span>;
}
function SortControl({
  sortBy,
  onChange
}: {
  sortBy: RosterSort;
  onChange: (s: RosterSort) => void;
}) {
  const options: Array<{
    k: RosterSort;
    label: string;
  }> = [{
    k: 'production',
    label: 'Production'
  }, {
    k: 'submissions',
    label: 'Subs'
  }, {
    k: 'leads',
    label: 'Leads'
  }, {
    k: 'login',
    label: 'Login'
  }, {
    k: 'name',
    label: 'Name'
  }];
  return <div className="flex items-center gap-1 text-sm" data-id="element-196">
      <span className="text-xs uppercase tracking-[0.22em] text-ink-50 mr-3" data-id="element-197">
        Sort
      </span>
      {options.map((o, i) => <Fragment key={o.k} data-id="element-198">
          {i > 0 && <span className="text-ink-30 mx-2" data-id="element-199">·</span>}
          <button onClick={() => onChange(o.k)} className={`relative pb-1 transition-colors hover:text-ink ${sortBy === o.k ? 'text-ink' : 'text-ink-50'}`} data-id="element-200">
            {o.label}
            {sortBy === o.k && <motion.span layoutId="sort-underline" className="absolute left-0 right-0 -bottom-0.5 h-px bg-signal" data-id="element-201" />}
          </button>
        </Fragment>)}
    </div>;
}
function TierSection({
  tier,
  officers,
  collapsed,
  onToggle,
  onSelect
}: {
  tier: TierKey;
  officers: ProcessedOfficer[];
  collapsed: boolean;
  onToggle: () => void;
  onSelect: (name: string) => void;
}) {
  return <div className="border-t border-ink-10 pt-6" data-id="element-202">
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between mb-5 group" data-id="element-203">
        <div className="flex items-baseline gap-3" data-id="element-204">
          <span className="w-2.5 h-2.5 rounded-full self-center" style={{
          backgroundColor: TIER_DOT[tier]
        }} data-id="element-205" />
          <h3 className="font-serif font-bold text-2xl text-primary" data-id="element-206">
            {TIER_LABELS[tier]}
          </h3>
          <span className="text-xs uppercase tracking-[0.22em] text-primary/50 tabular-nums font-bold" data-id="element-207">
            {officers.length} officer{officers.length === 1 ? '' : 's'}
          </span>
        </div>
        <motion.div animate={{
        rotate: collapsed ? -90 : 0
      }} transition={{
        duration: 0.2
      }} className="text-ink-50 group-hover:text-ink transition-colors" data-id="element-208">
          <ChevronDown className="w-5 h-5" strokeWidth={1.5} data-id="element-209" />
        </motion.div>
      </button>

      <AnimatePresence initial={false} data-id="element-210">
        {!collapsed && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }} className="overflow-hidden" data-id="element-211">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-px bg-ink-10 border border-ink-10 mb-1" data-id="element-212">
              <AnimatePresence mode="popLayout" data-id="element-213">
                {officers.map((o, i) => <OfficerCard key={o.name} officer={o} index={i} onClick={() => onSelect(o.name)} data-id="element-214" />)}
              </AnimatePresence>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function OfficerCard({
  officer,
  index,
  onClick
}: {
  officer: ProcessedOfficer;
  index: number;
  onClick: () => void;
}) {
  const ringColor = TIER_DOT[officer.tier];
  const isPacesetter = officer.tier === 'pacesetter';
  const isCoaching = officer.tier === 'coaching';
  const heavyWrap = officer.wrapUpRatio > 0.08;
  const delay = 0.05 + Math.min(index, 11) * 0.015;
  return <motion.button layout type="button" onClick={onClick} initial={{
    opacity: 0,
    y: 6
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.3,
    delay
  }} whileHover={{
    y: -2
  }} className="bg-bone text-left p-5 transition-colors hover:bg-bone-2/40 group" data-id="element-215">
      {/* Top row: avatar + production score */}
      <div className="flex items-start justify-between mb-3" data-id="element-216">
        <div className="relative flex-shrink-0" data-id="element-217">
          <div className="w-11 h-11 rounded-full bg-bone-2 flex items-center justify-center font-serif text-sm text-ink" style={{
          boxShadow: `0 0 0 2px ${ringColor}`
        }} data-id="element-218">
            {getInitials(officer.name)}
          </div>
          {isPacesetter && <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-signal flex items-center justify-center" data-id="element-219">
              <Star className="w-2.5 h-2.5 text-bone" fill="currentColor" strokeWidth={0} data-id="element-220" />
            </div>}
        </div>
        <div className="flex flex-col items-end" data-id="element-221">
          <div className={`font-serif text-3xl tabular-nums leading-none ${isCoaching ? 'text-ember' : 'text-ink'}`} data-id="element-222">
            {officer.productionScore.toFixed(0)}
          </div>
          <div className="text-[9px] uppercase tracking-[0.22em] text-ink-50 mt-1" data-id="element-223">
            Production
          </div>
        </div>
      </div>

      {/* Name */}
      <div className="font-serif text-base text-ink truncate" data-id="element-224">
        {officer.name}
      </div>

      {/* Output line */}
      <div className="text-xs text-ink-70 tabular-nums mt-1 mb-3" data-id="element-225">
        <span className="text-ink font-medium" data-id="element-226">{officer.leadsConverted}</span>
        <span className="text-ink-50" data-id="element-227"> leads</span>
        <span className="text-ink-30 mx-1.5" data-id="element-228">·</span>
        <span className="text-ink font-medium" data-id="element-229">{officer.submissions}</span>
        <span className="text-ink-50" data-id="element-230"> subs</span>
      </div>

      {/* Mini stacked bar */}
      <MiniBar officer={officer} delay={delay + 0.1} data-id="element-231" />

      {/* Footer */}
      <div className="flex items-center justify-between mt-3 text-[11px] uppercase tracking-[0.16em] font-medium" data-id="element-232">
        <span className="text-ink-50 tabular-nums" data-id="element-233">
          {formatHm(officer.loginMin)} login
        </span>
        {officer.isWarmPhone && <span className="inline-flex items-center gap-1 text-ember" data-id="element-234">
            <PhoneOff className="w-3 h-3" strokeWidth={2.5} data-id="element-235" />
            warm phone
          </span>}
        {!officer.isWarmPhone && heavyWrap && !officer.hasPartialData && <span className="inline-flex items-center gap-1 text-wrap" data-id="element-236">
            <AlertCircle className="w-3 h-3" strokeWidth={2.5} data-id="element-237" />
            heavy wrap
          </span>}
        {officer.hasPartialData && <span className="inline-flex items-center gap-1 text-ember" data-id="element-238">
            <AlertCircle className="w-3 h-3" strokeWidth={2.5} data-id="element-239" />
            partial
          </span>}
      </div>
    </motion.button>;
}
function MiniBar({
  officer,
  delay
}: {
  officer: ProcessedOfficer;
  delay: number;
}) {
  const total = officer.loginMin;
  if (total <= 0) {
    return <div className="h-2 rounded-full bg-bone-2" data-id="element-240" />;
  }
  return <div className="relative h-2 rounded-full overflow-hidden bg-bone-2 flex" data-id="element-241">
      {SEGMENTS.map((seg, segIdx) => {
      const value = officer[seg.key];
      if (!value || value <= 0) return null;
      const pct = value / total * 100;
      return <motion.div key={seg.key} initial={{
        width: 0
      }} animate={{
        width: `${pct}%`
      }} transition={{
        duration: 0.6,
        delay: delay + segIdx * 0.03,
        ease: [0.22, 1, 0.36, 1]
      }} style={{
        backgroundColor: seg.color
      }} className="h-full" data-id="element-242" />;
    })}
    </div>;
}